/**
 * Excel workbook operations (Office.js Excel API).
 * All functions assume Office.onReady has resolved in an Excel host.
 */

export interface RangeData {
  address: string
  values: unknown[][]
}

export async function getSelectedRange(): Promise<RangeData> {
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange()
    range.load(['address', 'values'])
    await context.sync()
    return { address: range.address, values: range.values }
  })
}

export async function readRange(address: string): Promise<RangeData> {
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet()
    const range = sheet.getRange(address)
    range.load(['address', 'values'])
    await context.sync()
    return { address: range.address, values: range.values }
  })
}

export interface WorkbookSheetSnapshot {
  name: string
  /** Actual used-range extent from A1 (may exceed the mirrored window). */
  rowCount: number
  columnCount: number
  /** Formatted display text of the mirrored window, row-major from A1. */
  text: string[][]
  /** True when the used range extends beyond the mirrored window. */
  truncated: boolean
}

/**
 * Read every visible worksheet's used range as display text, windowed from A1
 * so mirrored row/column indexes always equal real sheet coordinates. Caps
 * bound the read (and the mirror) on huge workbooks; actual used dimensions
 * are reported so the agent knows what it cannot see.
 */
export async function getWorkbookSnapshot(
  maxRows: number,
  maxCols: number,
  maxTotalCells: number
): Promise<WorkbookSheetSnapshot[]> {
  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets
    worksheets.load('items/name,items/visibility')
    await context.sync()

    const visible = worksheets.items.filter((sheet) => sheet.visibility === 'Visible')
    const usedRanges = visible.map((sheet) => {
      const used = sheet.getUsedRangeOrNullObject()
      used.load(['isNullObject', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount'])
      return used
    })
    await context.sync()

    let cellBudget = maxTotalCells
    const windows = visible.map((sheet, i) => {
      const used = usedRanges[i]
      if (used.isNullObject) return { name: sheet.name, rowCount: 0, columnCount: 0, window: undefined }
      // Window from A1 to the used range's bottom-right corner.
      const rowCount = used.rowIndex + used.rowCount
      const columnCount = used.columnIndex + used.columnCount
      const cols = Math.min(columnCount, maxCols)
      const rows = Math.max(0, Math.min(rowCount, maxRows, Math.floor(cellBudget / Math.max(cols, 1))))
      cellBudget -= rows * cols
      const window = rows > 0 ? sheet.getRangeByIndexes(0, 0, rows, cols) : undefined
      window?.load('text')
      return { name: sheet.name, rowCount, columnCount, window }
    })
    await context.sync()

    return windows.map(({ name, rowCount, columnCount, window }) => {
      const text = window ? (window.text as string[][]) : []
      return {
        name,
        rowCount,
        columnCount,
        text,
        truncated: text.length < rowCount || (text[0]?.length ?? 0) < columnCount
      }
    })
  })
}

/**
 * Re-mirror triggers: workbook content changes (not selection — reading every
 * used range on selection changes would be far too heavy). Collection-level
 * events need ExcelApi 1.9; resolves false when unavailable so the caller can
 * fall back to coarser refresh triggers. Best-effort cleanup via the disposer.
 */
export async function addWorkbookChangeHandlers(
  onChange: () => void
): Promise<{ supported: boolean; dispose: () => void }> {
  try {
    const registrations: OfficeExtension.EventHandlerResult<unknown>[] = []
    await Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets
      registrations.push(
        worksheets.onChanged.add(async () => onChange()) as OfficeExtension.EventHandlerResult<unknown>,
        worksheets.onAdded.add(async () => onChange()) as OfficeExtension.EventHandlerResult<unknown>,
        worksheets.onDeleted.add(async () => onChange()) as OfficeExtension.EventHandlerResult<unknown>
      )
      await context.sync()
    })
    return {
      supported: true,
      dispose: () => {
        void Excel.run(async (context) => {
          registrations.forEach((registration) => registration.remove())
          await context.sync()
        }).catch(() => undefined)
      }
    }
  } catch {
    return { supported: false, dispose: () => undefined }
  }
}

/**
 * Write a 2D array of values starting at `address` (e.g. "A1" or "Sheet1!B2").
 * The target range is sized to match the supplied values.
 */
export async function writeRange(address: string, values: unknown[][]): Promise<string> {
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet()
    const anchor = sheet.getRange(address)
    const rows = values.length
    const cols = values.reduce((max, row) => Math.max(max, row.length), 0)
    const target = anchor.getAbsoluteResizedRange(rows, cols)
    target.values = values
    target.load('address')
    await context.sync()
    return target.address
  })
}
